import type { ReactNode } from "react";
import { getAwardDefinition } from "@averagejoesff/shared";
import type { SeasonArchive } from "@averagejoesff/shared";
import { StateMessage } from "../../components/StateMessage";
import { useManagerNames } from "../../lib/useManagerNames";
import { BlowoutSeasonSummary } from "./BlowoutSeasonSummary";
import { BlowoutWeeklyTable } from "./BlowoutWeeklyTable";

interface BlowoutViewProps {
  archive: SeasonArchive;
  /** The season picker, rendered alongside this view's own intro — see App.tsx. */
  toolbar?: ReactNode;
}

export function BlowoutView({ archive, toolbar }: BlowoutViewProps) {
  const definition = getAwardDefinition("biggestBlowout");
  const award = archive.awards.biggestBlowout;
  const { labelByTeam } = useManagerNames(archive);

  if (!award) {
    return (
      <>
        {/* No feature__intro to share a line with here — keep the picker
            visible so switching seasons stays reachable. */}
        {toolbar && <div className="toolbar">{toolbar}</div>}
        <StateMessage
          variant="empty"
          title="No blowout data for this season"
          detail="The fetcher has not produced this award for the selected season yet."
        />
      </>
    );
  }

  return (
    <section className="feature" aria-labelledby="blowout-heading">
      <div className="feature__intro">
        <div className="feature__intro-row">
          <h2 id="blowout-heading" className="feature__title">
            {definition.label}
          </h2>
          {toolbar}
        </div>
        {definition.description && <p className="feature__description">{definition.description}</p>}
      </div>

      <h3 className="feature__subhead">Week by week</h3>
      <BlowoutWeeklyTable weeks={award.weeks} labelByTeam={labelByTeam} />

      <h3 className="feature__subhead">Season tally</h3>
      <BlowoutSeasonSummary standings={award.standings} labelByTeam={labelByTeam} />
    </section>
  );
}
